import { useState, useEffect, useCallback, useMemo } from "react";

import { GlobalEvents } from "../constants/Events";
import { EventBus } from "../events/EventBus";
import { loadServicesConfig } from "../services/config/loadConfig";
import { ServiceRegistry } from "../services/ServiceRegistry";
import debugLogger from "../utils/debugLogger";

import { loadStateMachineConfig } from "./config/loadStateMachineConfig";
import { GateManager } from "./GateManager";
import { StateMachine } from "./StateMachine";
import { createStateMachineConfig } from "./StateMachineConfig";
import {
  AppState,
  StateEvent,
  AppPhase,
  StateDataRequirements,
  StateAllowedRoutes,
} from "./types";

interface StateMachineHookOptions {
  eventBus?: EventBus;
  serviceRegistry?: ServiceRegistry;
  gateManager?: GateManager;
  autoStart?: boolean;
  onStateChange?: (from: AppState, to: AppState, event: StateEvent) => void;
  onError?: (error: Error, context: string) => void;
}

interface StateMachineHookReturn {
  // State
  currentState: AppState;
  currentPhase: AppPhase | null;
  dataRequirements: StateDataRequirements | null;
  allowedRoutes: StateAllowedRoutes | null;

  // Actions
  transition: (event: StateEvent) => boolean;
  canTransition: (event: StateEvent) => boolean;
  getAvailableEvents: () => StateEvent[];
  reset: () => void;

  // Navigation
  isRouteAllowed: (path: string) => boolean;
  getRedirectPath: () => string | null;

  // Utilities
  isInPhase: (state: AppState) => boolean;
  getPhaseDescription: () => string;
  getStateMachine: () => StateMachine;
}

export function useStateMachine(
  options: StateMachineHookOptions = {},
): StateMachineHookReturn {
  const {
    eventBus,
    serviceRegistry,
    gateManager,
    autoStart = true,
    onStateChange,
    onError,
  } = options;

  // State for config loading
  const [configLoaded, setConfigLoaded] = useState(false);
  const [initializationServices, setInitializationServices] = useState<
    string[]
  >([]);

  // Create state machine instance with async config loading
  const stateMachine = useMemo(() => {
    // Start with default config, will be updated when YAML loads
    const config = createStateMachineConfig();

    // Ensure config is valid
    if (!config) {
      throw new Error("Failed to create state machine configuration");
    }

    // Add custom transition callback
    const originalOnTransition = config.onTransition || undefined;
    config.onTransition = (from: AppState, to: AppState, event: StateEvent) => {
      // Call original callback
      if (originalOnTransition) {
        originalOnTransition(from, to, event);
      }

      // Call custom callback
      if (onStateChange) {
        onStateChange(from, to, event);
      }

      // Emit global event if eventBus is available
      if (eventBus) {
        eventBus.emitGlobal(GlobalEvents.STATE_CHANGED, {
          from,
          to,
          event,
          timestamp: new Date().toISOString(),
        });
      }
    };

    const sm = new StateMachine(config);

    // Set gate manager if available
    if (gateManager) {
      sm.setGateManager(gateManager);
    }

    return sm;
  }, [eventBus, onStateChange, gateManager]);

  // Load configs asynchronously
  useEffect(() => {
    const loadConfigs = async () => {
      try {
        // Load state machine config from YAML
        const {
          config: _smConfig,
          gates,
          initializationServices: initServices,
          transitionGates,
        } = await loadStateMachineConfig();

        // Initialize gate manager with gates from config
        if (gateManager && gates) {
          gateManager.initialize(gates);
          debugLogger.debug("Gate manager initialized with gates from config");
        }

        // Set transition gates on state machine
        if (stateMachine && transitionGates) {
          for (const { from, event, gate } of transitionGates) {
            stateMachine.setTransitionGate(from, event, gate);
          }
          debugLogger.debug(
            `Set ${transitionGates.length} transition gates from config`,
          );
        }

        // Store initialization services list
        setInitializationServices(initServices || []);
        setConfigLoaded(true);
        debugLogger.debug("State machine config loaded successfully from YAML");
      } catch (error) {
        debugLogger.warn(
          "Failed to load state machine config from YAML, using defaults:",
          error,
        );
        // Use default config - gates will use defaults
        if (gateManager) {
          // Initialize with empty gates (all open by default)
          gateManager.initialize({});
        }
        setConfigLoaded(true);
      }
    };

    if (stateMachine && gateManager) {
      void loadConfigs();
    }
  }, [gateManager, stateMachine]);

  // React state for current state
  const [currentState, setCurrentState] = useState<AppState>(
    stateMachine.getState(),
  );
  const [currentPhase, setCurrentPhase] = useState<AppPhase | null>(
    stateMachine.getCurrentPhase(),
  );
  const [dataRequirements, setDataRequirements] =
    useState<StateDataRequirements | null>(stateMachine.getDataRequirements());
  const [allowedRoutes, setAllowedRoutes] = useState<StateAllowedRoutes | null>(
    stateMachine.getAllowedRoutes(),
  );

  // Update state when state machine changes
  const updateState = useCallback(() => {
    setCurrentState(stateMachine.getState());
    setCurrentPhase(stateMachine.getCurrentPhase());
    setDataRequirements(stateMachine.getDataRequirements());
    setAllowedRoutes(stateMachine.getAllowedRoutes());
  }, [stateMachine]);

  // Transition function
  const transition = useCallback(
    (event: StateEvent): boolean => {
      try {
        // Check if transition is possible before attempting
        if (!stateMachine.canTransition(event)) {
          return false;
        }

        stateMachine.transition(event);
        updateState();
        return true;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown transition error";
        debugLogger.error("State machine transition failed:", errorMessage);

        if (onError) {
          onError(
            error instanceof Error ? error : new Error(errorMessage),
            "transition",
          );
        }

        // Emit error event
        if (eventBus) {
          eventBus.emitGlobal(StateEvent.ERROR_OCCURRED, {
            error: errorMessage,
            context: "state_machine_transition",
            event,
            timestamp: new Date().toISOString(),
          });
        }

        return false;
      }
    },
    [stateMachine, updateState, onError, eventBus],
  );

  // Check if transition is possible
  const canTransition = useCallback(
    (event: StateEvent): boolean => {
      return stateMachine.canTransition(event);
    },
    [stateMachine],
  );

  // Get available events
  const getAvailableEvents = useCallback((): StateEvent[] => {
    return stateMachine.getAvailableEvents();
  }, [stateMachine]);

  // Reset state machine
  const reset = useCallback(() => {
    try {
      stateMachine.reset();
      updateState();

      // Emit reset event
      if (eventBus) {
        eventBus.emitGlobal(GlobalEvents.STATE_MACHINE_RESET, {
          timestamp: new Date().toISOString(),
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown reset error";
      debugLogger.error("State machine reset failed:", errorMessage);

      if (onError) {
        onError(
          error instanceof Error ? error : new Error(errorMessage),
          "reset",
        );
      }
    }
  }, [stateMachine, updateState, eventBus, onError]);

  // Check if route is allowed in current state
  const isRouteAllowed = useCallback(
    (path: string): boolean => {
      const routes = stateMachine.getAllowedRoutes();
      if (!routes) return false;

      return routes.routes.some((route) => {
        // Handle wildcard routes
        if (route.endsWith("*")) {
          const baseRoute = route.slice(0, -1);
          return path.startsWith(baseRoute);
        }

        // Handle exact matches
        return route === path;
      });
    },
    [stateMachine],
  );

  // Get redirect path for current state
  const getRedirectPath = useCallback((): string | null => {
    const routes = stateMachine.getAllowedRoutes();
    return routes?.redirectTo || null;
  }, [stateMachine]);

  // Check if current state matches given state
  const isInPhase = useCallback(
    (state: AppState): boolean => {
      return currentState === state;
    },
    [currentState],
  );

  // Get current phase description
  const getPhaseDescription = useCallback((): string => {
    return currentPhase?.description || "Unknown phase";
  }, [currentPhase]);

  // Get state machine instance
  const getStateMachine = useCallback((): StateMachine => {
    return stateMachine;
  }, [stateMachine]);

  /**
   * Check if phase activities should be blocked due to closed exit gates
   * This works for all phases automatically
   */
  const shouldBlockPhaseActivities = useCallback(
    (state: AppState): boolean => {
      // Check if any exit gate from the specified state is closed
      return stateMachine.isExitGateClosed(state);
    },
    [stateMachine],
  );

  // Auto-start initialization if enabled
  useEffect(() => {
    if (!configLoaded) return; // Wait for config to load

    if (autoStart && currentState === AppState.INITIALIZATION) {
      // Emit app start event
      if (eventBus) {
        eventBus.emitGlobal(StateEvent.APP_START, {
          timestamp: new Date().toISOString(),
        });
      }

      // Start initialization process with service loading
      const startInitialization = async () => {
        try {
          // Check if exit gate is closed - if so, block phase activities
          // This works for all phases automatically via shouldBlockPhaseActivities
          if (shouldBlockPhaseActivities(currentState)) {
            debugLogger.debug(
              `Exit gate from ${currentState} is closed - blocking phase activities and staying in current phase`,
            );
            // Gate is closed - don't run phase activities or transition
            return;
          }

          // Gate is open - proceed with phase activities
          if (serviceRegistry && initializationServices.length > 0) {
            try {
              // Load service configs
              const serviceConfigs = await loadServicesConfig();

              // Register services
              for (const serviceConfig of serviceConfigs) {
                // Only register services listed in initialization phase
                if (initializationServices.includes(serviceConfig.name)) {
                  serviceRegistry.register(serviceConfig);
                }
              }

              // Initialize all registered services
              await serviceRegistry.initialize();

              debugLogger.debug("Services initialized successfully");
            } catch (serviceError) {
              debugLogger.error("Service initialization failed:", serviceError);
              throw serviceError;
            }
          }

          // Transition to recovery phase (StateMachine will check gate)
          transition(StateEvent.INITIALIZATION_COMPLETE);
        } catch (error) {
          debugLogger.error("Auto-initialization failed:", error);

          // Check if we can transition to ERROR state
          // Use StateMachine's canTransition to check gates
          if (canTransition(StateEvent.INITIALIZATION_FAILED)) {
            transition(StateEvent.INITIALIZATION_FAILED);
          } else {
            debugLogger.debug(
              "Initialization exit gate is closed - staying in INITIALIZATION phase despite error",
            );
            // Gate is closed - stay in current phase
            return;
          }
        }
      };

      void startInitialization();
    }
  }, [
    autoStart,
    currentState,
    eventBus,
    transition,
    configLoaded,
    serviceRegistry,
    gateManager,
    initializationServices,
  ]);

  // Listen for global state events
  useEffect(() => {
    if (!eventBus) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleGlobalStateEvent = (payload: any) => {
      // Handle state machine events
      const eventName = payload?.event || payload?.type;
      if (
        eventName &&
        Object.values(StateEvent).includes(eventName as StateEvent)
      ) {
        const stateEvent = eventName as StateEvent;

        // Only handle events that can be transitioned to from current state
        if (canTransition(stateEvent)) {
          transition(stateEvent);
        }
      }
    };

    // Subscribe to global events using the correct method
    const subscriptionId = eventBus.subscribeGlobal(
      "*",
      handleGlobalStateEvent,
    );

    return () => {
      // Note: EventBus doesn't have unsubscribe method, so we'll need to handle this differently
      // For now, we'll just log that we're cleaning up
      debugLogger.debug(
        "Cleaning up state machine event subscription:",
        subscriptionId,
      );
    };
  }, [eventBus, canTransition, transition]);

  // Debug logging in development
  useEffect(() => {
    if (import.meta.env.DEV) {
      debugLogger.debug("State Machine State:", {
        currentState,
        currentPhase: currentPhase?.state,
        dataRequirements: dataRequirements?.requiredModules,
        allowedRoutes: allowedRoutes?.routes,
      });
    }
  }, [currentState, currentPhase, dataRequirements, allowedRoutes]);

  return {
    // State
    currentState,
    currentPhase,
    dataRequirements,
    allowedRoutes,

    // Actions
    transition,
    canTransition,
    getAvailableEvents,
    reset,

    // Navigation
    isRouteAllowed,
    getRedirectPath,

    // Utilities
    isInPhase,
    getPhaseDescription,
    getStateMachine,
  };
}
